import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type { UserSecurityPostureDataTableItem } from '@kirin/api-client/contracts/user-security-posture';
import { queryKeys } from '@/hooks/api/queryKeys';
import { ClientDetailsModal } from '@/components/Inventory/UserSecurityPostureTab/ClientDetailsModal';
import { UserEndpointsDialog } from '@/components/Inventory/UserEndpointsDialog';
import { useTimestampFormat } from '@/hooks/useTimestampFormat';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import {
  createUserSecurityPostureColumns,
  createEndpointsColumn,
  createAgentVersionColumn,
} from './columns';
import { fetchUserSecurityPostureDataTable } from './api';
import { sanitizeUspParams } from './sanitizeUspParams';
import { UserExpandedContent } from './ExpandedContent';
import { USP_TABLE_CONFIG } from './constants';

interface DataTableUsersContentProps {
  organizationId: string;
}

export function DataTableUsersContent({
  organizationId,
}: DataTableUsersContentProps): ReactElement {
  const [selectedUser, setSelectedUser] =
    useState<UserSecurityPostureDataTableItem | null>(null);
  // The endpoints dialog opens from a row's count cell (KIR-3035). The old
  // Inventory deep-link entry (?endpointUser/&highlightEndpoint) was removed in
  // KIR-3911 along with the links that produced it.
  const [clickedEndpointsUser, setClickedEndpointsUser] = useState<{
    userId: string;
    userName: string;
  } | null>(null);

  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
  ]);
  const endpointsEnabled =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;

  const endpointsTarget = clickedEndpointsUser;

  const closeEndpointsDialog = useCallback(() => {
    setClickedEndpointsUser(null);
  }, []);

  const handleViewClientDetails = useCallback(
    (user: UserSecurityPostureDataTableItem) => {
      setSelectedUser(user);
    },
    []
  );

  const handleViewEndpoints = useCallback(
    (user: UserSecurityPostureDataTableItem) => {
      setClickedEndpointsUser({
        userId: user.userId,
        userName: user.name || 'Unknown User',
      });
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setSelectedUser(null);
  }, []);

  const { value: timestampFormat } = useTimestampFormat();

  const columns = useMemo(() => {
    const base = createUserSecurityPostureColumns(
      organizationId,
      handleViewClientDetails,
      timestampFormat
    );
    if (!endpointsEnabled) {
      return base;
    }
    // Insert the Endpoints + Agent version columns right after the Alerts column.
    const alertsIdx = base.findIndex(c => c.accessor === 'alertCount');
    const endpointsColumn = createEndpointsColumn(
      handleViewEndpoints,
      organizationId
    );
    const agentVersionColumn = createAgentVersionColumn();
    const insertAt = alertsIdx >= 0 ? alertsIdx + 1 : base.length;
    return [
      ...base.slice(0, insertAt),
      endpointsColumn,
      agentVersionColumn,
      ...base.slice(insertAt),
    ];
  }, [
    organizationId,
    handleViewClientDetails,
    handleViewEndpoints,
    timestampFormat,
    endpointsEnabled,
  ]);

  // KIR-3035: drop a stale endpoints filter/sort from the URL when the column
  // is flag-off, so it can't reach the query key or the request.
  const sanitizeParams = useCallback(
    (params: DataFetchParams): DataFetchParams =>
      sanitizeUspParams(params, endpointsEnabled),
    [endpointsEnabled]
  );

  const getQueryKeysFromParams = useCallback(
    (params: DataFetchParams) => {
      const sanitized = sanitizeParams(params);
      return queryKeys.userSecurityPosture.dataTable(organizationId, {
        page: sanitized.pagination.pageIndex + 1,
        limit: sanitized.pagination.pageSize,
        filters: sanitized.filters,
        sortBy: sanitized.sort?.field,
        sortOrder: sanitized.sort?.order,
        search: sanitized.search,
      });
    },
    [organizationId, sanitizeParams]
  );

  const renderExpandedContent = useCallback(
    (row: UserSecurityPostureDataTableItem) => (
      <UserExpandedContent row={row} />
    ),
    []
  );

  const queryConfig = useMemo(
    () => ({ staleTime: 30000, refetchOnWindowFocus: false }),
    []
  );

  const searchConfig = useMemo(
    () => ({ placeholder: 'Search users...', className: 'w-[260px]' }),
    []
  );

  return (
    <>
      <DataTable<UserSecurityPostureDataTableItem>
        id={USP_TABLE_CONFIG.tableId}
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        tableLayout="auto"
        columns={columns}
        errorBoundaryLabel="Something went wrong in the users table"
        search={searchConfig}
        features={{ enableSorting: true, enableRowExpansion: true }}
        rowExpansion={{
          render: renderExpandedContent,
          getRowId: row => row.userId,
          expandOnRowClick: true,
          showContainer: false,
          contentClassName: '',
        }}
        queryFn={(params, signal) =>
          fetchUserSecurityPostureDataTable(
            organizationId,
            sanitizeParams(params),
            signal
          )
        }
        getQueryKeysFromParams={getQueryKeysFromParams}
        noDataLabel="No users found."
        queryConfig={queryConfig}
      />
      <ClientDetailsModal
        userName={selectedUser?.name || 'Unknown User'}
        clients={selectedUser?.clients ?? []}
        open={selectedUser !== null}
        onClose={handleCloseModal}
      />
      {endpointsEnabled && (
        <UserEndpointsDialog
          organizationId={organizationId}
          userId={endpointsTarget?.userId ?? null}
          userName={endpointsTarget?.userName || 'Unknown User'}
          open={endpointsTarget !== null}
          onClose={closeEndpointsDialog}
        />
      )}
    </>
  );
}

DataTableUsersContent.displayName = 'DataTableUsersContent';

export default function DataTableUsersTab({
  organizationId,
}: DataTableUsersContentProps): ReactElement | null {
  const params = useParams<{ organizationId: string }>();
  const orgId = organizationId ?? params.organizationId;

  if (!orgId) {
    return null;
  }

  return <DataTableUsersContent key={orgId} organizationId={orgId} />;
}
